import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getFirestore, doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';

const firestore = getFirestore();

const GRUPOS_SANGUINEOS = ['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'];
const ESTADOS_SEGURO = ['Activo', 'Inactivo', 'Pendiente', 'En trámite', 'No aplica'];

const IMAGE_FIELDS = [
    { key: 'fotoCarnet', label: 'Foto Carnet' },
    { key: 'ciFrente', label: 'CI Frente' },
    { key: 'ciDorso', label: 'CI Dorso' },
    { key: 'registroFrente', label: 'Registro Frente' },
    { key: 'registroDorso', label: 'Registro Dorso' },
];

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function EditFuncionario() {
    const { id } = useParams();
    const [docRefId, setDocRefId] = useState(id);
    const [state, setState] = useState({
        nombre: '',
        nroCedula: '',
        correo: '',
        ciudad: '',
        grupoSanguineo: '',
        nombreContacto: '',
        seguroKemi: '',
        seguroSancor: '',
        latitud: '',
        longitud: '',
        telefono: '',
        contactoEmergencia: '',
        spinner: true,
        submitted: false,
    });
    const [existingUrls, setExistingUrls] = useState({
        fotoCarnet: '',
        ciFrente: '',
        ciDorso: '',
        registroFrente: '',
        registroDorso: '',
    });
    const [images, setImages] = useState({
        fotoCarnet: null,
        ciFrente: null,
        ciDorso: null,
        registroFrente: null,
        registroDorso: null,
    });
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });
    const [previewImage, setPreviewImage] = useState(null);
    const [previewLabel, setPreviewLabel] = useState("");

    useEffect(() => {
        const loadFuncionario = async () => {
            try {
                // Intento 1: buscar por document ID exacto
                let targetDocId = id;
                let docRef = doc(firestore, "funcionarios", targetDocId);
                let docSnap = await getDoc(docRef);

                // Intento 2: si no existe, buscar por el campo numérico 'id'
                if (!docSnap.exists()) {
                    const numId = Number(id);
                    if (!isNaN(numId)) {
                        const q = query(collection(firestore, "funcionarios"), where("id", "==", numId));
                        const querySnap = await getDocs(q);
                        if (!querySnap.empty) {
                            docSnap = querySnap.docs[0];
                            targetDocId = docSnap.id;
                        }
                    }
                }

                if (docSnap && docSnap.exists()) {
                    setDocRefId(targetDocId);
                    const data = docSnap.data();
                    setState(prev => ({
                        ...prev,
                        nombre: data.nombre || '',
                        nroCedula: data.nroCedula || '',
                        correo: data.correo || '',
                        ciudad: data.ciudad || '',
                        grupoSanguineo: data.grupoSanguineo || '',
                        nombreContacto: data.nombreContacto || '',
                        seguroKemi: data.seguroKemi || '',
                        seguroSancor: data.seguroSancor || '',
                        latitud: data.latitud || '',
                        longitud: data.longitud || '',
                        telefono: data.telefono || '',
                        contactoEmergencia: data.contactoEmergencia || '',
                        spinner: false,
                    }));
                    setExistingUrls({
                        fotoCarnet: data.fotoCarnet || '',
                        ciFrente: data.ciFrente || '',
                        ciDorso: data.ciDorso || '',
                        registroFrente: data.registroFrente || '',
                        registroDorso: data.registroDorso || '',
                    });
                } else {
                    setState(prev => ({ ...prev, spinner: false }));
                    setSnackbarInfo({ text: 'No se encontró el funcionario especificado', type: 'error', open: true });
                }
            } catch (error) {
                console.error("Error al cargar funcionario:", error);
                setState(prev => ({ ...prev, spinner: false }));
                setSnackbarInfo({ text: 'Error al cargar los datos del funcionario', type: 'error', open: true });
            }
        };
        loadFuncionario();
    }, [id]);

    const onChangeValues = (e) => {
        const { name, value } = e.target;
        setState(prev => ({ ...prev, [name]: value }));
    };

    const onChangeImage = (key, file) => {
        setImages(prev => ({ ...prev, [key]: file }));
    };

    const uploadImage = async (targetId, key, file) => {
        if (!file) return null;
        const rawExt = file.name && file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
        const cleanExt = rawExt.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
        const path = `funcionarios/${targetId}/${key}_${Date.now()}.${cleanExt}`;
        const fileRef = storageRef(storage, path);
        const metadata = {
            contentType: file.type || 'image/jpeg',
        };
        await uploadBytes(fileRef, file, metadata);
        return await getDownloadURL(fileRef);
    };

    const updateFuncionario = async () => {
        setState(prev => ({ ...prev, spinner: true }));
        try {
            const imageUrls = {};
            const targetId = docRefId || id;
            for (const { key } of IMAGE_FIELDS) {
                if (images[key]) {
                    imageUrls[key] = await uploadImage(targetId, key, images[key]);
                } else {
                    imageUrls[key] = existingUrls[key] || null;
                }
            }

            const docRefTarget = doc(firestore, "funcionarios", String(targetId));
            const dataToSave = {
                nombre: state.nombre || '',
                nroCedula: state.nroCedula || '',
                correo: state.correo || '',
                ciudad: state.ciudad || '',
                grupoSanguineo: state.grupoSanguineo || '',
                nombreContacto: state.nombreContacto || '',
                seguroKemi: state.seguroKemi || '',
                seguroSancor: state.seguroSancor || '',
                latitud: state.latitud || '',
                longitud: state.longitud || '',
                telefono: state.telefono || '',
                contactoEmergencia: state.contactoEmergencia || '',
                ...imageUrls,
            };

            await setDoc(docRefTarget, dataToSave, { merge: true });
            setState(prev => ({ ...prev, spinner: false, submitted: true }));
            setSnackbarInfo({ text: 'Funcionario actualizado correctamente!', type: 'success', open: true });
        } catch (error) {
            console.error("Error al actualizar funcionario:", error);
            setState(prev => ({ ...prev, spinner: false }));
            const isStoragePermissionError = error?.code === 'storage/unauthorized' || 
                (error?.message && error.message.includes('unauthorized'));
            const errorMsg = isStoragePermissionError
                ? 'Error de permisos en Firebase Storage (storage/unauthorized). Verifique las Reglas de Seguridad en Firebase Console.'
                : 'Ocurrió un error al actualizar el funcionario';
            setSnackbarInfo({ text: errorMsg, type: 'error', open: true });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    if (state.spinner) return <CircularProgress />;

    return (
        <Container component="main" maxWidth="md">
            {state.submitted ? (
                <div>
                    <Typography variant="h4">Funcionario actualizado correctamente!</Typography>
                    <Button href="/funcionarios">Ir al listado</Button>
                </div>
            ) : (
                <div className="form-container">
                    <Typography component="h1" variant="h5">Editar Funcionario</Typography>
                    <div className="order-container">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    required
                                    fullWidth
                                    className="default__textfield"
                                    id="nombre"
                                    label="Nombre completo"
                                    value={state.nombre}
                                    name="nombre"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    required
                                    fullWidth
                                    className="default__textfield"
                                    id="nroCedula"
                                    label="Número de cédula"
                                    value={state.nroCedula}
                                    name="nroCedula"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="correo"
                                    label="Correo"
                                    type="email"
                                    value={state.correo}
                                    name="correo"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="ciudad"
                                    label="Ciudad"
                                    value={state.ciudad}
                                    name="ciudad"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="grupoSanguineo"
                                    label="Grupo sanguíneo"
                                    value={state.grupoSanguineo}
                                    name="grupoSanguineo"
                                    onChange={onChangeValues}
                                >
                                    <MenuItem value="">-</MenuItem>
                                    {GRUPOS_SANGUINEOS.map(g => (
                                        <MenuItem key={g} value={g}>{g}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="telefono"
                                    label="Teléfono"
                                    value={state.telefono}
                                    name="telefono"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="nombreContacto"
                                    label="Nombre del contacto"
                                    value={state.nombreContacto}
                                    name="nombreContacto"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="contactoEmergencia"
                                    label="Teléfono del contacto"
                                    value={state.contactoEmergencia}
                                    name="contactoEmergencia"
                                    onChange={onChangeValues}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="seguroKemi"
                                    label="Estado seguro Kemi (médico)"
                                    value={state.seguroKemi}
                                    name="seguroKemi"
                                    onChange={onChangeValues}
                                >
                                    <MenuItem value="">-</MenuItem>
                                    {ESTADOS_SEGURO.map(e => (
                                        <MenuItem key={e} value={e}>{e}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="seguroSancor"
                                    label="Estado seguro Sancor (accidentes)"
                                    value={state.seguroSancor}
                                    name="seguroSancor"
                                    onChange={onChangeValues}
                                >
                                    <MenuItem value="">-</MenuItem>
                                    {ESTADOS_SEGURO.map(e => (
                                        <MenuItem key={e} value={e}>{e}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="latitud"
                                    label="Latitud (coordenadas casa)"
                                    value={state.latitud}
                                    name="latitud"
                                    onChange={onChangeValues}
                                    placeholder="-31.4201"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    className="default__textfield"
                                    id="longitud"
                                    label="Longitud (coordenadas casa)"
                                    value={state.longitud}
                                    name="longitud"
                                    onChange={onChangeValues}
                                    placeholder="-64.1888"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Documentación (imágenes)</Typography>
                                {IMAGE_FIELDS.map(({ key, label }) => (
                                    <Grid item xs={12} sm={6} key={key}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                startIcon={<CloudUploadIcon />}
                                                size="small"
                                            >
                                                {label}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    hidden
                                                    onChange={(e) => onChangeImage(key, e.target.files?.[0] || null)}
                                                />
                                            </Button>
                                            <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {images[key]?.name || (existingUrls[key] ? 'Imagen cargada' : 'Sin archivo')}
                                            </Typography>
                                            {existingUrls[key] && (
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => {
                                                        setPreviewImage(existingUrls[key]);
                                                        setPreviewLabel(label);
                                                    }} 
                                                    title="Ver imagen"
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {(existingUrls[key] || images[key]) && (
                                                <IconButton 
                                                    size="small" 
                                                    color="error"
                                                    onClick={() => {
                                                        onChangeImage(key, null);
                                                        setExistingUrls(prev => ({ ...prev, [key]: '' }));
                                                    }} 
                                                    title="Eliminar imagen"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Button
                            type="button"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className="button__save"
                            onClick={updateFuncionario}
                            disabled={!state.nombre || !state.nroCedula}
                        >
                            Guardar cambios
                        </Button>
                    </div>
                </div>
            )}
            <Snackbar open={snackbarInfo.open} autoHideDuration={2000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>

            <Dialog 
                open={Boolean(previewImage)} 
                onClose={() => setPreviewImage(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{previewLabel}</span>
                    <IconButton onClick={() => setPreviewImage(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 3, pt: 1 }}>
                    {previewImage && (
                        <img 
                            src={previewImage} 
                            alt={previewLabel} 
                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '4px' }} 
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
}

export default EditFuncionario;
